import { useCallback, useMemo } from 'react';
import { AccountDialog, type AccountFormValues } from '@/components/settings/AccountDialog';
import type { Account, AccountConfig } from '@/types/email';
import { triggerAccountFastRecovery, triggerAccountSync } from '@/api/sync';
import { useAccountStore } from '@/stores/accountStore';
import { useSignatureStore } from '@/stores/signatureStore';
import { useUIStore } from '@/stores/uiStore';
import { useAccountModalsStore } from '@/stores/accountModalsStore';
import { getErrorMessage } from '@/lib/error';

function buildFallbackConfig(account: Account | null): AccountConfig | null {
  if (!account) {
    return null;
  }
  return {
    id: account.id,
    emailAddress: account.emailAddress,
    displayName: account.displayName ?? null,
    provider: account.provider ?? 'imap',
    status: account.status,
    hasCredentials: Boolean(account.hasCredentials),
    color: account.color ?? null,
    imap: null,
    smtp: null,
  };
}

export function AccountModalsHost() {
  const accounts = useAccountStore((state) => state.accounts);
  const addAccount = useAccountStore((state) => state.addAccount);
  const addingAccount = useAccountStore((state) => state.addingAccount);
  const updateAccountData = useAccountStore((state) => state.updateAccountData);
  const updatingAccount = useAccountStore((state) => state.updatingAccount);
  const signatures = useSignatureStore((state) => state.signatures);
  const addAccountOpen = useUIStore((state) => state.addAccountOpen);
  const setAddAccountOpen = useUIStore((state) => state.setAddAccountOpen);
  const editAccountOpen = useUIStore((state) => state.editAccountOpen);
  const setEditAccountOpen = useUIStore((state) => state.setEditAccountOpen);
  const setSettingsDialogOpen = useUIStore((state) => state.setSettingsDialogOpen);
  const setSettingsInitialTab = useUIStore((state) => state.setSettingsInitialTab);

  const editingAccountId = useAccountModalsStore((state) => state.editingAccountId);
  const editingAccountConfig = useAccountModalsStore((state) => state.editingAccountConfig);
  const editingAccountLoading = useAccountModalsStore((state) => state.editingAccountLoading);
  const fullRefreshPending = useAccountModalsStore((state) => state.fullRefreshPending);
  const fastRecoveryPending = useAccountModalsStore((state) => state.fastRecoveryPending);
  const setFullRefreshPending = useAccountModalsStore((state) => state.setFullRefreshPending);
  const setFastRecoveryPending = useAccountModalsStore((state) => state.setFastRecoveryPending);
  const loadAccountConfig = useAccountModalsStore((state) => state.loadAccountConfig);
  const resetEditingState = useAccountModalsStore((state) => state.resetEditingState);
  const setGlobalError = useUIStore((state) => state.setGlobalError);

  const editingAccount = useMemo(() => {
    if (!editingAccountId) {
      return null;
    }
    return accounts.find((account) => account.id === editingAccountId) ?? null;
  }, [accounts, editingAccountId]);

  const effectiveConfig = editingAccountConfig ?? buildFallbackConfig(editingAccount);

  const handleAddAccount = useCallback(
    async (values: AccountFormValues) => {
      await addAccount({
        email: values.email,
        password: values.password,
        provider: values.provider,
        displayName: values.displayName ?? undefined,
        color: values.color ?? undefined,
        defaultSignatureId: values.defaultSignatureId ?? undefined,
        settings: values.settings,
      });
      setAddAccountOpen(false);
      setSettingsInitialTab('accounts');
      setSettingsDialogOpen(true);
    },
    [addAccount, setAddAccountOpen, setSettingsDialogOpen, setSettingsInitialTab]
  );

  const handleUpdateAccount = useCallback(
    async (values: AccountFormValues) => {
      if (!editingAccountId) {
        return;
      }
      await updateAccountData(editingAccountId, {
        email: values.email,
        password: values.password,
        displayName: values.displayName ?? null,
        provider: values.provider,
        color: values.color ?? null,
        defaultSignatureId: values.defaultSignatureId ?? null,
        settings: values.settings,
      });
        setEditAccountOpen(false);
      resetEditingState();
    },
    [editingAccountId, resetEditingState, setEditAccountOpen, updateAccountData]
  );

  const handleFullRefresh = useCallback(async () => {
    if (!editingAccountId) {
      return;
    }
    setFullRefreshPending(true);
    try {
      await triggerAccountSync(editingAccountId);
    } finally {
      setFullRefreshPending(false);
    }
  }, [editingAccountId, setFullRefreshPending]);

  const handleFastRecovery = useCallback(async () => {
    if (!editingAccountId) {
      return;
    }
    setFastRecoveryPending(true);
    try {
      await triggerAccountFastRecovery(editingAccountId);
    } catch (error) {
      setGlobalError(getErrorMessage(error) ?? 'Failed to trigger fast recovery');
      throw error;
    } finally {
      setFastRecoveryPending(false);
    }
  }, [editingAccountId, setFastRecoveryPending, setGlobalError]);

  const handleEditModalOpenChange = useCallback(
    async (open: boolean) => {
      setEditAccountOpen(open);
      if (!open) {
        resetEditingState();
        setSettingsInitialTab('accounts');
        setSettingsDialogOpen(true);
        return;
      }
      if (editingAccountId) {
        await loadAccountConfig(editingAccountId);
      }
    },
    [editingAccountId, loadAccountConfig, resetEditingState, setEditAccountOpen, setSettingsDialogOpen, setSettingsInitialTab]
  );

  const handleManageSignatures = useCallback(() => {
    setSettingsInitialTab('signatures');
    setSettingsDialogOpen(true);
  }, [setSettingsDialogOpen, setSettingsInitialTab]);

  return (
    <>
      <AccountDialog
        mode="add"
        open={addAccountOpen}
        onOpenChange={(open) => {
          setAddAccountOpen(open);
          if (!open) {
            setSettingsInitialTab('accounts');
            setSettingsDialogOpen(true);
          }
        }}
        onSubmit={handleAddAccount}
        isSubmitting={addingAccount}
        signatures={signatures}
        onManageSignatures={handleManageSignatures}
      />

      <AccountDialog
        mode="edit"
        open={editAccountOpen}
        onOpenChange={handleEditModalOpenChange}
        onSubmit={handleUpdateAccount}
        isSubmitting={updatingAccount}
        isLoading={editingAccountLoading}
        account={editingAccount}
        initialConfig={effectiveConfig}
        onFastRecovery={handleFastRecovery}
        fastRecoveryPending={fastRecoveryPending}
        onFullRefresh={handleFullRefresh}
        fullRefreshPending={fullRefreshPending}
        signatures={signatures}
        onManageSignatures={handleManageSignatures}
      />
    </>
  );
}
